// Central navigation config — single source of truth for all nav rendering
// Roles: 'finance_admin' | 'ops_admin' | 'staff'
// visibleTo: array of roles that can SEE the section/item
// writableBy: array of roles that can WRITE/POST (used by pages to show/hide action buttons)

export const NAV_SECTIONS = [
  {
    key: 'farm',
    label: 'Farm Operations',
    icon: '🌱',
    defaultOpen: true,
    visibleTo: ['finance_admin', 'ops_admin', 'staff'],
    items: [
      { key: 'dashboard',    to: '/dashboard',     icon: '🏠', label: 'Dashboard',        visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
      { key: 'tasks',        to: '/tasks',          icon: '✅', label: 'Tasks',             visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
      { key: 'farm-log',     to: '/farm-log',       icon: '📋', label: 'Farm Log',          visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
      { key: 'crop-cycles',  to: '/crop-cycles',    icon: '🔄', label: 'Crop Cycles',       visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'harvest',      to: '/harvest',        icon: '🌾', label: 'Harvest Log',       visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
      { key: 'inventory',    to: '/inventory',      icon: '📦', label: 'Inventory',         visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'quality',      to: '/quality',        icon: '🔬', label: 'Quality Control',   visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
      { key: 'incidents',    to: '/incidents',      icon: '🚨', label: 'Incident Reports',  visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
      { key: 'timesheets',   to: '/timesheets',     icon: '⏱️', label: 'Timesheets',        visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
      { key: 'staff',        to: '/staff-register', icon: '👥', label: 'Staff Register',    visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'visitors',     to: '/visitors',       icon: '🪪', label: 'Visitor Log',       visibleTo: ['finance_admin', 'ops_admin', 'staff'] },
    ],
  },
  {
    key: 'sales',
    label: 'Sales & Cash',
    icon: '💰',
    defaultOpen: true,  // Open by default — critical for daily ops and cash recording
    visibleTo: ['finance_admin', 'ops_admin'],
    items: [
      { key: 'customers',    to: '/customers',      icon: '🤝', label: 'Customers',         visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'sales-orders', to: '/sales-orders',   icon: '🛒', label: 'Sales Orders',      visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'invoices',     to: '/invoices',       icon: '📄', label: 'Invoices',          visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'logistics',    to: '/logistics',      icon: '🚚', label: 'Logistics',         visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'cash-log',     to: '/cash-log',       icon: '💵', label: 'Cash Log',          visibleTo: ['finance_admin', 'ops_admin'] },
    ],
  },
  {
    key: 'purchasing',
    label: 'Purchasing',
    icon: '🏪',
    defaultOpen: false,
    visibleTo: ['finance_admin', 'ops_admin'],
    items: [
      { key: 'requisitions', to: '/requisitions',   icon: '📋', label: 'Requisitions',      visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'procurement',  to: '/procurement',    icon: '📦', label: 'Purchase Orders',   visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'grn',          to: '/goods-received', icon: '📥', label: 'Goods Received',    visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'payables',     to: '/payables',       icon: '💸', label: 'Payables',          visibleTo: ['finance_admin', 'ops_admin'] },
    ],
  },
  {
    key: 'finance',
    label: 'Finance & Control',
    icon: '📊',
    defaultOpen: false,
    visibleTo: ['finance_admin', 'ops_admin'],
    items: [
      { key: 'weekly-pl',    to: '/weekly-pl',            icon: '📈', label: 'Weekly P&L',         visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'statements',   to: '/financial-statements', icon: '📊', label: 'Financial Statements', visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'journal',      to: '/journal-ledger',       icon: '📒', label: 'Journal Ledger',      visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'assets',       to: '/fixed-assets',         icon: '🏗️', label: 'Fixed Assets',        visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'loans',        to: '/loans',                icon: '🏦', label: 'Loans',               visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'equity',       to: '/equity',               icon: '💎', label: 'Equity & Capital',    visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'period-close', to: '/period-close',         icon: '🔒', label: 'Period Close',        visibleTo: ['finance_admin'] },
    ],
  },
  {
    key: 'admin',
    label: 'Admin & Governance',
    icon: '⚙️',
    defaultOpen: false,
    visibleTo: ['finance_admin', 'ops_admin'],
    items: [
      { key: 'admin-users',    to: '/admin/users',    icon: '👥', label: 'Manage Users',    visibleTo: ['finance_admin'] },
      { key: 'admin-settings', to: '/admin/settings', icon: '⚙️', label: 'Settings',        visibleTo: ['finance_admin'] },
      { key: 'admin-reports',  to: '/admin/reports',  icon: '📈', label: 'Reports',         visibleTo: ['finance_admin'] },
      { key: 'sops',           to: '/sops',           icon: '📖', label: 'SOPs & Guides',   visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'emergency',      to: '/emergency',      icon: '🆘', label: 'Emergency Plan',  visibleTo: ['finance_admin', 'ops_admin'] },
      { key: 'ml-insights',    to: '/ml-insights',    icon: '🧠', label: 'Smart Insights',  visibleTo: ['finance_admin', 'ops_admin'] },
    ],
  },
]

// Which roles can WRITE to finance modules (post journals, record equity, period close etc.)
export const FINANCE_WRITE_ROLES = ['finance_admin']

// Which roles can APPROVE (invoices, payments, requisitions etc.)
export const APPROVAL_ROLES = ['finance_admin', 'ops_admin']

// Default dashboard route by role
export const DEFAULT_DASHBOARD = {
  finance_admin: '/dashboard/finance',
  ops_admin: '/dashboard/ops',
  staff: '/dashboard/staff',
}

// Helper — filter nav sections/items for a given user_role
export const getNavForRole = (userRole) => {
  const role = userRole || 'staff'
  return NAV_SECTIONS
    .filter(s => s.visibleTo.includes(role))
    .map(s => ({
      ...s,
      items: s.items.filter(i => i.visibleTo.includes(role))
    }))
}

// Helper — can this role write to finance?
export const canWriteFinance = (userRole) => FINANCE_WRITE_ROLES.includes(userRole)

// Helper — can this role approve?
export const canApprove = (userRole) => APPROVAL_ROLES.includes(userRole)
